import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { sampleProperties, propertyTypes, featuredProperties } from "@/mocks/properties";
import { buildWhatsAppUrl } from "@/lib/whatsapp";

const categoryImages: Record<string, { src: string; alt: string }> = {
  Kominka: {
    src: "https://readdy.ai/api/search-image?query=Traditional%20Japanese%20kominka%20farmhouse%20exterior%20with%20exposed%20wooden%20beams%20and%20dark%20tiled%20roof%20small%20garden%20soft%20overcast%20light%20muted%20warm%20palette%20documentary%20architectural%20photography%20no%20people%20no%20on-image%20text%20no%20logos&width=600&height=400&seq=noren-cat-kominka&orientation=landscape",
    alt: "Traditional Japanese kominka farmhouse exterior, representative category image",
  },
  Houses: {
    src: "https://readdy.ai/api/search-image?query=Modest%20two-storey%20Japanese%20suburban%20house%20on%20a%20quiet%20residential%20lane%20overcast%20sky%20understated%20muted%20warm%20tones%20documentary%20architectural%20photography%20no%20people%20no%20on-image%20text%20no%20logos&width=600&height=400&seq=noren-cat-houses&orientation=landscape",
    alt: "Japanese suburban house on a quiet lane, representative category image",
  },
  "Apartment blocks": {
    src: "https://readdy.ai/api/search-image?query=Small%20two-storey%20Japanese%20apartment%20building%20in%20a%20regional%20town%20with%20simple%20architecture%20calm%20plain%20atmosphere%20overcast%20light%20muted%20warm%20tones%20documentary%20photography%20no%20people%20no%20on-image%20text%20no%20logos&width=600&height=400&seq=noren-cat-apartment&orientation=landscape",
    alt: "Small Japanese apartment building in a regional town, representative category image",
  },
  Condos: {
    src: "https://readdy.ai/api/search-image?query=Bright%20minimal%20Japanese%20one%20room%20condo%20interior%20with%20clean%20lines%20soft%20daylight%20through%20large%20windows%20no%20clutter%20muted%20warm%20earth%20tones%20editorial%20interior%20photography%20no%20people%20no%20on-image%20text%20no%20logos&width=600&height=400&seq=noren-cat-condo&orientation=landscape",
    alt: "Minimal Japanese condo interior with clean lines, representative category image",
  },
};

export default function ListingsSection() {
  const [activeFilter, setActiveFilter] = useState("all");
  const { t, i18n } = useTranslation();

  // For home: featured first, then up to 9 total (so up to ~3 featured + non-featured fill)
  const filtered = (
    activeFilter === "all"
      ? [...featuredProperties.slice(0, 6), ...sampleProperties.filter((p) => !p.featured)]
      : sampleProperties.filter((p) => p.type === activeFilter)
  ).slice(0, 9);

  return (
    <section id="listings" className="w-full bg-ink py-20 md:py-28 scroll-mt-20">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10 md:mb-14">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold mb-3">
              {t("listings.sectionLabel")}
            </p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-[2.75rem] text-paper leading-[1.1]">
              {t("listings.title")}
              <br />
              <span className="italic font-normal text-paper/70">{t("listings.titleItalic")}</span>
            </h2>
          </div>
          <p className="text-sm text-paper/50 max-w-sm leading-relaxed font-light">
            {t("listings.intro")}
          </p>
        </div>

        <div className="flex flex-wrap gap-2 mb-8 md:mb-10">
          {propertyTypes.map((type) => (
            <button
              key={type.value}
              onClick={() => setActiveFilter(type.value)}
              className={`px-4 py-2 text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer border ${
                activeFilter === type.value
                  ? "bg-gold text-ink border-gold"
                  : "bg-transparent text-paper/60 border-paper/20 hover:border-paper/40"
              }`}
            >
              {type.label === "All" ? t("type.all") :
               type.label === "Kominka" ? t("type.kominka") :
               type.label === "Detached Houses" ? t("type.detached") :
               type.label === "Apartment Blocks" ? t("type.apartmentBlocks") :
               type.label === "Condos" ? t("type.condos") : type.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {filtered.map((property) => {
            const catImg = categoryImages[property.type] || categoryImages.Houses;
            return (
              <div
                key={property.id}
                className={`group transition-all duration-300 hover:-translate-y-1 ${
                  property.featured
                    ? "bg-ink-light border-2 border-vermilion/40 hover:border-vermilion/70 shadow-[0_0_0_1px_rgba(196,77,52,0.1)]"
                    : "bg-ink-light border border-white/10 hover:border-white/20"
                }`}
              >
                <div className="relative overflow-hidden bg-ink-light">
                  <img
                    src={property.image || catImg.src}
                    alt={property.image ? property.headline : catImg.alt}
                    loading="lazy"
                    className="w-full h-52 md:h-56 object-cover object-center group-hover:scale-[1.03] transition-transform duration-500"
                    style={{ objectPosition: "center" }}
                  />
                  {property.featured && (
                    <span className="absolute top-3 left-3 bg-vermilion text-paper text-xs font-semibold px-3 py-1.5 flex items-center gap-1">
                      <i className="ri-star-fill text-[10px]"></i>
                      おすすめ
                    </span>
                  )}
                  {!property.featured && (
                    <span className="absolute top-3 left-3 bg-paper/10 backdrop-blur-sm text-xs font-semibold px-3 py-1.5 text-paper/80 border border-paper/10">
                      {property.region}
                    </span>
                  )}
                  <span className="absolute top-3 right-3 bg-ink/70 backdrop-blur-sm text-paper/80 text-xs font-semibold px-3 py-1.5">
                    {property.featured ? property.region.split(" · ")[0] : t("label.offMarket")}
                  </span>
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs text-paper/40 uppercase tracking-wider">
                      {property.typeLabel}
                    </span>
                  </div>
                  <p className="font-serif text-base font-medium text-paper/90 mb-1 leading-snug">
                    {property.headline}
                  </p>
                  <p className="text-sm text-paper/40 leading-relaxed mb-4 line-clamp-2 font-light">
                    {property.description}
                  </p>
                  <div className="flex items-baseline gap-2 mb-4">
                    <span className="font-serif text-xl font-semibold text-paper">
                      {property.priceUSD}
                    </span>
                    <span className="text-sm text-paper/40">
                      ({property.priceJPY})
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-paper/40 mb-4 font-light">
                    {property.type === "Apartment blocks" ? (
                      <span className="flex items-center gap-1">
                        <i className="ri-building-line"></i>
                        {property.highlight || "Block"}
                      </span>
                    ) : property.bedrooms > 0 ? (
                      <span className="flex items-center gap-1">
                        <i className="ri-hotel-bed-line"></i>
                        {t("label.bedrooms", { count: property.bedrooms })}
                      </span>
                    ) : null}
                    {property.area && property.area !== "—" && (
                      <span className="flex items-center gap-1">
                        <i className="ri-ruler-line"></i>
                        {property.area}
                      </span>
                    )}
                    {property.yearBuilt > 0 && (
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i>
                        {property.yearBuilt}
                      </span>
                    )}
                    {property.yieldPct && (
                      <span className="flex items-center gap-1 text-gold">
                        <i className="ri-line-chart-line"></i>
                        {property.yieldPct.toFixed(1)}%
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <Link
                      to={`/properties/${property.id}`}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-gold hover:text-paper transition-colors duration-300 cursor-pointer"
                    >
                      {t("btn.viewDetails")}
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                    <a
                      href={buildWhatsAppUrl(property.id, property.headline, i18n.language)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-green-600 hover:text-green-700 transition-colors duration-300"
                    >
                      <i className="ri-whatsapp-line"></i>
                      {t("btn.whatsapp")}
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-12 text-center">
          <p className="text-sm text-paper/40 mb-5 font-light">
            {t("listings.sampleNote")}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="/noren-property-portfolio.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-paper text-ink text-sm font-medium px-6 py-3 rounded-full hover:bg-gold transition-colors duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-file-download-line text-base"></i>
              Download portfolio PDF
              <span className="text-xs text-ink/50 ml-1">(7 MB · 29 pp)</span>
            </a>
            <a
              href="#inquiry"
              onClick={(e) => {
                e.preventDefault();
                document
                  .getElementById("inquiry")
                  ?.scrollIntoView({ behavior: "smooth" });
              }}
              className="inline-flex items-center gap-2 border border-paper/30 text-paper text-sm font-medium px-6 py-3 rounded-full hover:bg-paper hover:text-ink transition-colors duration-300 whitespace-nowrap cursor-pointer"
            >
              {t("btn.requestList")}
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}